'use client'

import * as React from 'react'
import { Separator, Button } from "@sarkiai/ui"
import { useTranslations } from "@/components/I18nProvider"
import { GlassBox } from "@/components/custom/box"
import { Shield, CheckCircle2 } from "lucide-react"
import Link from "next/link"

export default function PrivacyPage() {
  const t = useTranslations('privacy')

  return (
    <main className="flex min-h-screen flex-col bg-background">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-black text-white overflow-hidden text-center px-4 lg:px-12">
        <div className="absolute inset-0 bg-white/5 opacity-50" />
        <div className="container relative z-10 mx-auto">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="inline-flex items-center justify-center h-20 w-20 rounded-3xl bg-white/10 mb-4 backdrop-blur-xl shadow-2xl">
              <Shield className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-8xl  tracking-tighter uppercase leading-none">
              {t('page_title')}
            </h1>
            <p className="text-xl md:text-2xl opacity-70 max-w-2xl mx-auto leading-relaxed italic">
              {t('last_updated')}
            </p>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
      </section>

      <section className="py-20 md:py-32 px-4 lg:px-12">
        <div className="container mx-auto max-w-5xl">
          <GlassBox className="p-10 md:p-20 space-y-20 rounded-4xl border-black/5 dark:border-white/10 shadow-2xl shadow-black/5 bg-white/50 dark:bg-card/50 backdrop-blur-2xl text-foreground/80">
            {/* Introduction */}
            <section className="space-y-6">
              <p className="text-xl leading-relaxed first-letter:text-5xl first-letter: first-letter:mr-3 first-letter:float-left first-letter:text-foreground">
                {t('introduction')}
              </p>
            </section>

            <Separator className="bg-black/5 dark:bg-white/10 h-[1px]" />

            {/* Interpretation and Definitions */}
            <section className="space-y-10">
              <h2 className="text-3xl  uppercase tracking-tight text-foreground">{t('interpretation_definitions.title')}</h2>

              <div className="space-y-6">
                <h3 className="text-xl  uppercase tracking-wide flex items-center gap-3">
                  <span className="w-8 h-[1px] bg-black/20 dark:bg-white/20" />
                  {t('interpretation_definitions.interpretation.title')}
                </h3>
                <p className="leading-relaxed">
                  {t('interpretation_definitions.interpretation.content')}
                </p>
              </div>

              <div className="space-y-8 p-10 rounded-[2rem] bg-black/5 dark:bg-white/5">
                <h3 className="text-xl  uppercase tracking-wide flex items-center gap-3">
                  <span className="w-8 h-[1px] bg-black/20 dark:bg-white/20" />
                  {t('interpretation_definitions.definitions.title')}
                </h3>
                <p className="font-medium opacity-60">{t('interpretation_definitions.definitions.intro')}</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm leading-relaxed">
                  <li className="space-y-1">
                    <strong className="block text-foreground uppercase tracking-widest text-[10px]">{t('interpretation_definitions.definitions.labels.account')}</strong>
                    <span className="opacity-80">{t('interpretation_definitions.definitions.account')}</span>
                  </li>
                  <li className="space-y-1">
                    <strong className="block text-foreground uppercase tracking-widest text-[10px]">{t('interpretation_definitions.definitions.labels.company')}</strong>
                    <span className="opacity-80">{t('interpretation_definitions.definitions.company')}</span>
                  </li>
                  <li className="space-y-1">
                    <strong className="block text-foreground uppercase tracking-widest text-[10px]">{t('interpretation_definitions.definitions.labels.cookies')}</strong>
                    <span className="opacity-80">{t('interpretation_definitions.definitions.cookies')}</span>
                  </li>
                  <li className="space-y-1">
                    <strong className="block text-foreground uppercase tracking-widest text-[10px]">{t('interpretation_definitions.definitions.labels.country')}</strong>
                    <span className="opacity-80">{t('interpretation_definitions.definitions.country')}</span>
                  </li>
                  <li className="space-y-1">
                    <strong className="block text-foreground uppercase tracking-widest text-[10px]">{t('interpretation_definitions.definitions.labels.device')}</strong>
                    <span className="opacity-80">{t('interpretation_definitions.definitions.device')}</span>
                  </li>
                  <li className="space-y-1">
                    <strong className="block text-foreground uppercase tracking-widest text-[10px]">{t('interpretation_definitions.definitions.labels.personal_data')}</strong>
                    <span className="opacity-80">{t('interpretation_definitions.definitions.personal_data')}</span>
                  </li>
                  <li className="space-y-1">
                    <strong className="block text-foreground uppercase tracking-widest text-[10px]">{t('interpretation_definitions.definitions.labels.service')}</strong>
                    <span className="opacity-80">{t('interpretation_definitions.definitions.service')}</span>
                  </li>
                  <li className="space-y-1">
                    <strong className="block text-foreground uppercase tracking-widest text-[10px]">{t('interpretation_definitions.definitions.labels.website')}</strong>
                    <span className="opacity-80">{t('interpretation_definitions.definitions.website')}</span>
                  </li>
                </ul>
              </div>
            </section>

            <Separator className="bg-black/5 dark:bg-white/10 h-[1px]" />

            {/* Collecting and Using Personal Data */}
            <section className="space-y-10">
              <h2 className="text-3xl  uppercase tracking-tight text-foreground">{t('collecting_data.title')}</h2>

              <div className="space-y-12">
                <div className="space-y-6">
                  <h3 className="text-xl  uppercase tracking-wide flex items-center gap-3">
                    <span className="w-8 h-[1px] bg-black/20 dark:bg-white/20" />
                    {t('collecting_data.types.title')}
                  </h3>

                  <div className="space-y-6 p-10 rounded-[2rem] border border-black/5 dark:border-white/10">
                    <h4 className=" uppercase tracking-widest text-xs opacity-60 italic">{t('collecting_data.types.personal.title')}</h4>
                    <p className="leading-relaxed">
                      {t('collecting_data.types.personal.intro')}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        t('collecting_data.types.personal.email'),
                        t('collecting_data.types.personal.name'),
                        t('collecting_data.types.personal.phone'),
                        t('collecting_data.types.personal.address'),
                        t('collecting_data.types.personal.usage')
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-sm font-medium">
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="space-y-6">
                  <h4 className="text-xl  uppercase tracking-wide flex items-center gap-3">
                    <span className="w-8 h-[1px] bg-black/20 dark:bg-white/20" />
                    {t('collecting_data.types.usage.title')}
                  </h4>
                  <p className="leading-relaxed">
                    {t('collecting_data.types.usage.content')}
                  </p>
                </div>
              </div>
            </section>

            <Separator className="bg-black/5 dark:bg-white/10 h-[1px]" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
              <section className="space-y-6">
                <h2 className="text-2xl  uppercase tracking-tight text-foreground">{t('retention.title')}</h2>
                <p className="leading-relaxed italic opacity-80">
                  {t('retention.content')}
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl  uppercase tracking-tight text-foreground">{t('deletion.title')}</h2>
                <p className="leading-relaxed italic opacity-80">
                  {t('deletion.content')}{' '}
                  <Link href="/faq" className=" tracking-widest text-xs uppercase underline underline-offset-4 hover:opacity-70 transition-opacity">
                    {t('deletion.link_text')}
                  </Link>
                </p>
              </section>
            </div>

            <Separator className="bg-black/5 dark:bg-white/10 h-[1px]" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
              <section className="space-y-6">
                <h2 className="text-2xl  uppercase tracking-tight text-foreground">{t('security.title')}</h2>
                <p className="leading-relaxed">
                  {t('security.content')}
                </p>
              </section>

              <section className="space-y-6">
                <h2 className="text-2xl  uppercase tracking-tight text-foreground">{t('contact.title')}</h2>
                <div className="space-y-6">
                  <p className="leading-relaxed">
                    {t('contact.content')}
                  </p>
                  <Button asChild className="h-12 px-8 rounded-2xl bg-black dark:bg-white text-white dark:text-black  uppercase tracking-widest text-xs shadow-xl shadow-black/10 hover:bg-black/90 dark:hover:bg-white/90 transition-all">
                    <a href="mailto:contact@djotta.com">
                      contact@djotta.com
                    </a>
                  </Button>
                </div>
              </section>
            </div>
          </GlassBox>
        </div>
      </section>
    </main>
  )
}
